import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ConfirmDialog } from '@personal-dashboard/ui';
import useDashboards, { DashboardData } from '../../Dashboard/hooks/useDashboards';
import DashboardDialog from '../../Dashboard/components/DashboardDialog';
import { DASHBOARD_LAYOUTS, DashboardLayoutKey } from '../../Dashboard/layouts';

const DashboardsSection: React.FC = () => {
  const {
    dashboards,
    loading,
    error,
    handleCreate,
    handleRename,
    handleDelete,
    handleChangeLayout,
  } = useDashboards();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DashboardData | null>(null);
  const [deleting, setDeleting] = useState<DashboardData | null>(null);

  if (loading) return <Alert severity="info">Loading dashboards…</Alert>;
  if (error) return <Alert severity="error">Failed to load dashboards: {error.message}</Alert>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Dashboards ({dashboards.length})
        </Typography>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          New Dashboard
        </Button>
      </Stack>

      <DashboardDialog
        open={dialogOpen}
        mode="create"
        onCancel={() => setDialogOpen(false)}
        onCreate={(name, layoutKey) => {
          handleCreate(name, layoutKey);
          setDialogOpen(false);
        }}
        onRename={async () => undefined}
      />

      {editing && (
        <DashboardDialog
          open={!!editing}
          mode="rename"
          initialName={editing.name}
          onCancel={() => setEditing(null)}
          onCreate={async () => undefined}
          onRename={(name) => {
            handleRename(editing.id, name);
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Delete dashboard?"
        message={`This will permanently delete "${deleting?.name ?? ''}".`}
        confirmLabel="Delete"
        severity="error"
        onConfirm={() => {
          if (deleting) handleDelete(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />

      <Stack spacing={1}>
        {dashboards.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No dashboards yet — create one to get started.
          </Typography>
        )}
        {dashboards.map((d) => (
          <Paper key={d.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ flexGrow: 1, minWidth: 160 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {d.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {DASHBOARD_LAYOUTS.find((l) => l.key === d.layoutKey)?.label ?? d.layoutKey} ·{' '}
                  {d.slots.filter(Boolean).length} / {d.slots.length} slots filled
                </Typography>
              </Box>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Layout</InputLabel>
                <Select
                  label="Layout"
                  size="small"
                  value={d.layoutKey}
                  onChange={(e) => handleChangeLayout(d.id, e.target.value as DashboardLayoutKey)}
                >
                  {DASHBOARD_LAYOUTS.map((layout) => (
                    <MenuItem key={layout.key} value={layout.key}>
                      {layout.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={() => setEditing(d)} aria-label="Rename">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => setDeleting(d)} aria-label="Delete">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default DashboardsSection;
